/**
 * IdeaMax Promo storefront behaviour.
 *
 * Products carry no variant price ($0.00). Unit price lives in the
 * `custom.price_tiers_json` metafield keyed by quantity break, so the tier a
 * shopper picks is the only thing that determines the price we can show.
 */
(() => {
  const money = (amount) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  };

  /**
   * The quote list. Lives in localStorage because this storefront has no cart
   * and /products/<handle>.js carries no metafields — so the tier price and
   * imprint choices have to be snapshotted at the moment the shopper adds an
   * item, not looked up later.
   */
  const QuoteList = (() => {
    const KEY = 'idm:quote-list';
    const LIMIT = 50;

    const read = () => {
      try {
        const raw = JSON.parse(localStorage.getItem(KEY));
        return Array.isArray(raw) ? raw : [];
      } catch {
        return []; // Corrupt or unavailable (private mode) — start clean.
      }
    };

    const announce = () => document.dispatchEvent(new CustomEvent('idm:quote-list-change'));

    const write = (items) => {
      try {
        localStorage.setItem(KEY, JSON.stringify(items.slice(0, LIMIT)));
      } catch {
        /* Quota or private mode: the list simply doesn't persist. */
      }
      announce();
    };

    return {
      all: read,
      count: () => read().length,
      has: (handle) => read().some((i) => i.handle === handle),
      /** Re-adding a product replaces it, so the newest tier/imprint choice wins. */
      add(item) {
        const items = read().filter((i) => i.handle !== item.handle);
        items.push(item);
        write(items);
      },
      remove(handle) {
        write(read().filter((i) => i.handle !== handle));
      },
      setQty(handle, qty) {
        const items = read().map((i) => (i.handle === handle ? { ...i, qty } : i));
        write(items);
      },
      clear() {
        write([]);
      },
      total: () => read().reduce((sum, i) => sum + Number(i.unitPrice || 0) * Number(i.qty || 0), 0),
    };
  })();

  // Another tab changed the list — keep this one honest.
  window.addEventListener('storage', (event) => {
    if (event.key === 'idm:quote-list') {
      document.dispatchEvent(new CustomEvent('idm:quote-list-change'));
    }
  });

  /** Quantity input rendered by the theme's buy-buttons form, if present. */
  const quantityInput = (root) => {
    const section = root.closest('.shopify-section') || document;
    return section.querySelector('input[name="quantity"]');
  };

  const selectTier = (widget, tier, { silent = false } = {}) => {
    widget.querySelectorAll('[data-idm-tier]').forEach((el) => {
      const on = el === tier;
      el.classList.toggle('is-selected', on);
      el.setAttribute('aria-checked', on ? 'true' : 'false');
      el.tabIndex = on ? 0 : -1;
    });

    const qty = Number(tier.dataset.qty);
    const price = Number(tier.dataset.price);

    const input = quantityInput(widget);
    if (input && !silent) {
      input.value = String(qty);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    document.dispatchEvent(
      new CustomEvent('idm:tier-change', { detail: { qty, price, total: qty * price } })
    );
  };

  const initVolumePricing = (widget) => {
    if (widget.dataset.idmInit) return;
    widget.dataset.idmInit = '1';

    const tiers = [...widget.querySelectorAll('[data-idm-tier]')];
    if (!tiers.length) return;

    const moq = Number(widget.dataset.moq || 0);
    const initial = tiers.find((t) => Number(t.dataset.qty) >= moq) || tiers[0];
    selectTier(widget, initial, { silent: true });

    widget.addEventListener('click', (event) => {
      const tier = event.target.closest('[data-idm-tier]');
      if (tier) selectTier(widget, tier);
    });

    widget.addEventListener('keydown', (event) => {
      const tier = event.target.closest('[data-idm-tier]');
      if (!tier) return;

      const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
      if (step) {
        event.preventDefault();
        const next = tiers[(tiers.indexOf(tier) + step + tiers.length) % tiers.length];
        selectTier(widget, next);
        next.focus();
      } else if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        selectTier(widget, tier);
      }
    });
  };

  /** Mirrors the selected tier into the "estimated total" line above the CTA. */
  const initEstimate = (node) => {
    if (node.dataset.idmInit) return;
    node.dataset.idmInit = '1';

    const qtyEl = node.querySelector('[data-idm-est-qty]');
    const totalEl = node.querySelector('[data-idm-est-total]');

    document.addEventListener('idm:tier-change', ({ detail }) => {
      if (qtyEl) qtyEl.textContent = detail.qty.toLocaleString();
      if (totalEl) totalEl.textContent = money(detail.total);

      node.querySelectorAll('[data-idm-quote-qty]').forEach((input) => {
        input.value = String(detail.qty);
      });
    });
  };

  const imprintSelections = () => {
    const out = {};
    document.querySelectorAll('[data-idm-imprint]').forEach((select) => {
      if (select.value) out[select.dataset.idmImprint] = select.value;
    });
    return out;
  };

  const hiddenField = (form, name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `contact[${name}]`;
    input.value = value;
    input.dataset.idmContextField = '1';
    form.appendChild(input);
  };

  const formatList = (items) =>
    items
      .map((item, index) => {
        const line = [`${index + 1}. ${item.title}${item.itemNo ? ` (#${item.itemNo})` : ''}`];
        const lineTotal = Number(item.unitPrice || 0) * Number(item.qty || 0);
        line.push(`   ${item.qty} pcs x ${money(Number(item.unitPrice || 0))} = ${money(lineTotal)}`);
        const imprint = Object.entries(item.imprint || {});
        if (imprint.length) line.push(`   ${imprint.map(([k, v]) => `${k}: ${v}`).join(' | ')}`);
        if (item.url) line.push(`   ${item.url}`);
        return line.join('\n');
      })
      .join('\n');

  /**
   * Attaches everything the sales team needs to the contact form: the imprint
   * dropdowns on a product page, or the whole quote list on the list page.
   * Without this the shopper's choices never leave the browser.
   */
  const syncQuoteContext = (dialog) => {
    const form = dialog.querySelector('form');
    if (!form) return;

    form.querySelectorAll('[data-idm-context-field]').forEach((el) => el.remove());

    Object.entries(imprintSelections()).forEach(([name, value]) => hiddenField(form, name, value));

    if (dialog.closest('[data-idm-quote-list-form]')) {
      const items = QuoteList.all();
      if (!items.length) return;
      hiddenField(form, 'Items', String(items.length));
      hiddenField(form, 'Estimated total', money(QuoteList.total()));
      hiddenField(form, 'Quote list', formatList(items));
    }
  };

  const initQuoteModal = (root) => {
    if (root.dataset.idmQuoteInit) return;
    root.dataset.idmQuoteInit = '1';

    const dialog = root.querySelector('dialog[data-idm-quote-dialog]');
    if (!dialog) return;

    root.querySelectorAll('[data-idm-quote-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (dialog.open) return;
        syncQuoteContext(dialog);
        dialog.showModal();
      });
    });
    root.querySelectorAll('[data-idm-quote-close]').forEach((button) => {
      button.addEventListener('click', () => dialog.close());
    });

    // Backdrop click closes; clicks inside the panel must not bubble out to it.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    // Shopify posts the contact form as a full page load, so a rendered success
    // state means the shopper just submitted and should see it. Every dialog on
    // the page renders that state, so only the first one may open — showModal()
    // throws once another modal is already up.
    if (dialog.querySelector('[data-idm-quote-posted]')) {
      // The inquiry went out with the list attached; don't let it send twice.
      if (dialog.closest('[data-idm-quote-list-form]')) QuoteList.clear();
      if (!document.querySelector('dialog[open]')) dialog.showModal();
    }
  };

  const initTabs = (root) => {
    if (root.dataset.idmInit) return;
    root.dataset.idmInit = '1';

    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    if (!tabs.length) return;

    const activate = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        t.classList.toggle('is-active', on);
      });
      panels.forEach((panel) => {
        panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      });
    };

    activate(tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0]);

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
        activate(next);
        next.focus();
      });
    });
  };

  /** Discount-code copy button in the hero offer card. */
  const initCopy = (button) => {
    if (button.dataset.idmInit) return;
    button.dataset.idmInit = '1';

    const original = button.textContent.trim();
    button.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(button.dataset.idmCopy);
      } catch {
        return; // Clipboard denied (insecure context, or the user said no).
      }
      button.textContent = button.dataset.idmCopyDone || original;
      setTimeout(() => {
        button.textContent = original;
      }, 2000);
    });
  };

  /** Reads the tier the shopper picked, falling back to the cheapest one. */
  const selectedTier = (button) => {
    const widget = document.querySelector('[data-idm-volume-pricing]');
    const chosen = widget?.querySelector('[data-idm-tier].is-selected');
    if (chosen) return { qty: Number(chosen.dataset.qty), unitPrice: chosen.dataset.price };
    return { qty: Number(button.dataset.moq || 1), unitPrice: button.dataset.unitPrice || '0' };
  };

  const initAddToQuote = (button) => {
    if (button.dataset.idmInit) return;
    button.dataset.idmInit = '1';

    const addedLabel = button.dataset.addedLabel;
    const original = button.textContent.trim();

    const paint = () => {
      const inList = QuoteList.has(button.dataset.handle);
      button.classList.toggle('is-in-list', inList);
      if (addedLabel) button.textContent = inList ? addedLabel : original;
    };
    paint();
    document.addEventListener('idm:quote-list-change', paint);

    button.addEventListener('click', () => {
      const { qty, unitPrice } = selectedTier(button);
      QuoteList.add({
        handle: button.dataset.handle,
        title: button.dataset.title,
        itemNo: button.dataset.itemNo || '',
        image: button.dataset.image || '',
        url: button.dataset.url || '',
        moq: Number(button.dataset.moq || 0),
        qty,
        unitPrice,
        imprint: imprintSelections(),
      });
    });
  };

  const initQuoteCount = (el) => {
    if (el.dataset.idmInit) return;
    el.dataset.idmInit = '1';

    const paint = () => {
      const n = QuoteList.count();
      el.textContent = String(n);
      el.hidden = n === 0;
    };
    paint();
    document.addEventListener('idm:quote-list-change', paint);
  };

  const initQuoteListPage = (root) => {
    if (root.dataset.idmInit) return;
    root.dataset.idmInit = '1';

    const tbody = root.querySelector('[data-idm-list-items]');
    const empty = root.querySelector('[data-idm-list-empty]');
    const filled = root.querySelector('[data-idm-list-filled]');
    const totalEl = root.querySelector('[data-idm-list-total]');
    const rowTemplate = root.querySelector('template[data-idm-list-row]');
    if (!tbody || !rowTemplate) return;

    const render = () => {
      const items = QuoteList.all();
      if (empty) empty.hidden = items.length > 0;
      if (filled) filled.hidden = items.length === 0;

      tbody.replaceChildren();
      items.forEach((item) => {
        const row = rowTemplate.content.cloneNode(true);
        const set = (sel, value) => {
          const node = row.querySelector(sel);
          if (node) node.textContent = value;
        };

        const image = row.querySelector('[data-row-image]');
        if (image) {
          if (item.image) image.src = item.image;
          else image.remove();
        }

        const link = row.querySelector('[data-row-title]');
        if (link) {
          link.textContent = item.title;
          if (item.url) link.href = item.url;
        }

        set('[data-row-item-no]', item.itemNo ? `#${item.itemNo}` : '');
        set('[data-row-unit]', money(Number(item.unitPrice || 0)));
        set('[data-row-total]', money(Number(item.unitPrice || 0) * Number(item.qty || 0)));

        const imprint = Object.entries(item.imprint || {});
        const imprintEl = row.querySelector('[data-row-imprint]');
        if (imprintEl) {
          if (imprint.length) imprintEl.textContent = imprint.map(([k, v]) => `${k}: ${v}`).join(' · ');
          else imprintEl.remove();
        }

        const qty = row.querySelector('[data-row-qty]');
        if (qty) {
          qty.value = item.qty;
          qty.min = item.moq || 1;
          qty.addEventListener('change', () => {
            const next = Math.max(Number(qty.min) || 1, Number(qty.value) || 1);
            QuoteList.setQty(item.handle, next);
          });
        }

        row.querySelector('[data-row-remove]')?.addEventListener('click', () => {
          QuoteList.remove(item.handle);
        });

        tbody.appendChild(row);
      });

      if (totalEl) totalEl.textContent = money(QuoteList.total());
    };

    render();
    document.addEventListener('idm:quote-list-change', render);
    root.querySelector('[data-idm-list-clear]')?.addEventListener('click', () => QuoteList.clear());
  };

  const initTileSlider = (root) => {
    if (root.dataset.idmInit) return;
    root.dataset.idmInit = '1';

    const track = root.querySelector('[data-idm-tile-track]');
    const prev = root.querySelector('[data-idm-tile-prev]');
    const next = root.querySelector('[data-idm-tile-next]');
    if (!track) return;

    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 1;
      const overflow = max > 0;
      [prev, next].forEach((b) => b && (b.hidden = !overflow));
      if (!overflow) return;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max;
    };

    const step = () => Math.max(track.clientWidth * 0.85, 200);
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  };

  const boot = (scope = document) => {
    scope.querySelectorAll?.('[data-idm-volume-pricing]').forEach(initVolumePricing);
    scope.querySelectorAll?.('[data-idm-estimate]').forEach(initEstimate);
    scope.querySelectorAll?.('[data-idm-quote]').forEach(initQuoteModal);
    scope.querySelectorAll?.('[data-idm-tabs]').forEach(initTabs);
    scope.querySelectorAll?.('[data-idm-tile-slider]').forEach(initTileSlider);
    scope.querySelectorAll?.('[data-idm-copy]').forEach(initCopy);
    scope.querySelectorAll?.('[data-idm-add-to-quote]').forEach(initAddToQuote);
    scope.querySelectorAll?.('[data-idm-quote-count]').forEach(initQuoteCount);
    scope.querySelectorAll?.('[data-idm-quote-list]').forEach(initQuoteListPage);
  };

  document.addEventListener('DOMContentLoaded', () => boot());
  // Theme editor re-renders sections in place; re-bind whatever it swapped in.
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:block:select', (event) => boot(event.target));
  if (document.readyState !== 'loading') boot();
})();
